import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Crown,
  Star,
  Award,
  Search,
  Loader2,
  RefreshCcw,
  Tablet,
  User,
  Package,
  Filter,
  TrendingUp,
  CheckCircle2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US");

const TIER_META = {
  A_plus: {
    label: "A+",
    name: "زبون عادي",
    color: "#3B82F6",
    bg: "bg-[#E0F2FE]",
    text: "text-[#1E3A5F]",
    border: "border-[#3B82F6]/40",
    desc: "أسعار التجزئة العادية + مكافأة 150 د.ع لكل قطعة",
  },
  C_plus: {
    label: "C+",
    name: "زبون C+",
    color: "#0891B2",
    bg: "bg-[#CFFAFE]",
    text: "text-[#155E75]",
    border: "border-[#0891B2]/60",
    desc: "أسعار C+ (وسط بين العادي والجملة) + مكافأة 75 د.ع لكل قطعة",
  },
  B_plus: {
    label: "B+",
    name: "زبون جملة",
    color: "#F5A623",
    bg: "bg-[#FEF3C7]",
    text: "text-[#92400E]",
    border: "border-[#F5A623]/60",
    desc: "أسعار الجملة (wholesale) + مكافأة 50 د.ع لكل قطعة",
  },
};

// Tier cycle order when clicking the badge: A+ → C+ → B+ → A+
const NEXT_TIER = { A_plus: "C_plus", C_plus: "B_plus", B_plus: "A_plus" };

export default function TiersTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tierFilter, setTierFilter] = useState("all"); // all | A_plus | B_plus
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (tierFilter !== "all") params.tier = tierFilter;
      const { data } = await axios.get(`${API}/admin/tiers/overview`, { params });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "فشل تحميل التصنيفات");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tierFilter]);

  // debounce search box
  useEffect(() => {
    const t = setTimeout(() => load(), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const toggleTier = async (cust) => {
    const next = NEXT_TIER[cust.tier] || "A_plus";
    const label = next === "B_plus" ? "B+ (زبون جملة)" : next === "C_plus" ? "C+ (وسط)" : "A+ (زبون عادي)";
    if (!window.confirm(`تبديل تصنيف «${cust.customer_name || cust.phone}» إلى ${label}؟`)) return;
    setBusyId(cust.id);
    try {
      await axios.patch(`${API}/admin/tiers/${cust.id}`, { tier: next });
      toast.success(`تم — ${cust.customer_name || cust.phone} الآن ${next.replace("_plus", "+")}`);
      setData((d) => {
        if (!d) return d;
        const dec = (k) => (d.counts[k] || 0) - (cust.tier === ({A_plus:"a_plus",B_plus:"b_plus",C_plus:"c_plus"})[cust.tier] ? 1 : 0);
        const counts = {
          ...d.counts,
          a_plus: (d.counts.a_plus || 0) + (cust.tier === "A_plus" ? -1 : 0) + (next === "A_plus" ? 1 : 0),
          b_plus: (d.counts.b_plus || 0) + (cust.tier === "B_plus" ? -1 : 0) + (next === "B_plus" ? 1 : 0),
          c_plus: (d.counts.c_plus || 0) + (cust.tier === "C_plus" ? -1 : 0) + (next === "C_plus" ? 1 : 0),
        };
        // unused 'dec' linter satisfier
        void dec;
        return {
          ...d,
          customers: d.customers.map((c) => (c.id === cust.id ? { ...c, tier: next } : c)),
          counts,
        };
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "فشل التبديل");
    } finally {
      setBusyId(null);
    }
  };

  const counts = data?.counts || { total: 0, a_plus: 0, b_plus: 0, c_plus: 0 };
  const customers = data?.customers || [];

  const grouped = useMemo(() => customers, [customers]);

  return (
    <div className="space-y-4" dir="rtl" data-testid="tiers-tab">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-bl from-[#1E3A5F] via-[#3B82F6] to-[#1E3A5F] text-white p-5 shadow-2xl">
        <div className="absolute -top-10 -left-10 w-44 h-44 bg-yellow-300/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-10 -right-10 w-44 h-44 bg-amber-200/15 rounded-full blur-3xl pointer-events-none" />
        <div className="relative flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-yellow-200/90 text-xs font-bold tracking-wider uppercase">
              <Crown className="w-4 h-4" />
              التصنيفات
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold mt-1">
              تصنيفات الزبائن <span className="text-yellow-200">A+ / C+ / B+</span>
            </h1>
            <p className="text-white/85 text-xs mt-2 max-w-xl leading-relaxed">
              <b>A+</b>: عادي (150 د.ع/قطعة). <b>C+</b>: وسط (75 د.ع/قطعة). <b>B+</b>: جملة (50 د.ع/قطعة). الضغط على البادج يتنقّل: A+ → C+ → B+ → A+.
            </p>
          </div>
          <button
            data-testid="tiers-refresh-btn"
            onClick={load}
            className="bg-white/15 hover:bg-white/25 backdrop-blur px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1"
          >
            <RefreshCcw className="w-3.5 h-3.5" />
            تحديث
          </button>
        </div>
      </div>

      {/* Counts */}
      <div className="grid grid-cols-4 gap-2">
        <div
          onClick={() => setTierFilter("all")}
          data-testid="filter-all"
          className={`bg-white rounded-2xl p-2.5 border-2 shadow-sm cursor-pointer transition ${
            tierFilter === "all" ? "border-[#1E3A5F] shadow-md" : "border-transparent hover:border-slate-200"
          }`}
        >
          <div className="text-[10px] text-slate-500 font-bold">الإجمالي</div>
          <div className="font-extrabold text-xl text-[#1E3A5F] tabular-nums">{fmt(counts.total)}</div>
        </div>
        <div
          onClick={() => setTierFilter("A_plus")}
          data-testid="filter-a-plus"
          className={`rounded-2xl p-2.5 border-2 shadow-sm cursor-pointer transition ${
            tierFilter === "A_plus" ? "border-[#3B82F6] shadow-md bg-[#E0F2FE]" : "border-transparent bg-white hover:border-slate-200"
          }`}
        >
          <div className="text-[10px] text-[#1E3A5F] font-bold flex items-center gap-0.5">
            <User className="w-2.5 h-2.5" /> A+
          </div>
          <div className="font-extrabold text-xl text-[#1E3A5F] tabular-nums">{fmt(counts.a_plus)}</div>
        </div>
        <div
          onClick={() => setTierFilter("C_plus")}
          data-testid="filter-c-plus"
          className={`rounded-2xl p-2.5 border-2 shadow-sm cursor-pointer transition ${
            tierFilter === "C_plus" ? "border-[#0891B2] shadow-md bg-[#CFFAFE]" : "border-transparent bg-white hover:border-slate-200"
          }`}
        >
          <div className="text-[10px] text-[#155E75] font-bold flex items-center gap-0.5">
            <Award className="w-2.5 h-2.5" /> C+
          </div>
          <div className="font-extrabold text-xl text-[#155E75] tabular-nums">{fmt(counts.c_plus)}</div>
        </div>
        <div
          onClick={() => setTierFilter("B_plus")}
          data-testid="filter-b-plus"
          className={`rounded-2xl p-2.5 border-2 shadow-sm cursor-pointer transition ${
            tierFilter === "B_plus" ? "border-[#F5A623] shadow-md bg-[#FEF3C7]" : "border-transparent bg-white hover:border-slate-200"
          }`}
        >
          <div className="text-[10px] text-[#92400E] font-bold flex items-center gap-0.5">
            <Crown className="w-2.5 h-2.5" /> B+
          </div>
          <div className="font-extrabold text-xl text-[#92400E] tabular-nums">{fmt(counts.b_plus)}</div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          data-testid="tiers-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="ابحث باسم الزبون / الماركت / الهاتف / رقم الايباد"
          className="w-full bg-white border-2 border-slate-200 rounded-xl pr-10 pl-3 py-2.5 text-sm focus:border-[#3B82F6] outline-none"
        />
      </div>

      {/* List */}
      {loading && !data ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
        </div>
      ) : grouped.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-slate-200">
          <Filter className="w-12 h-12 mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500 text-sm">لا يوجد زبائن مطابقين</p>
        </div>
      ) : (
        <div className="space-y-2">
          {grouped.map((c) => {
            const meta = TIER_META[c.tier] || TIER_META.A_plus;
            const isBusy = busyId === c.id;
            return (
              <div
                key={c.id}
                data-testid={`tier-row-${c.id}`}
                className={`bg-white rounded-2xl shadow-sm border ${meta.border} p-3 flex items-center gap-3`}
              >
                {/* Tier badge (big) */}
                <button
                  type="button"
                  onClick={() => !isBusy && toggleTier(c)}
                  data-testid={`tier-toggle-${c.id}`}
                  disabled={isBusy}
                  title="اضغط لتبديل التصنيف"
                  className={`flex-shrink-0 w-16 h-16 rounded-2xl ${meta.bg} ${meta.text} border-2 ${meta.border} flex flex-col items-center justify-center font-extrabold transition active:scale-95 hover:shadow disabled:opacity-50 relative`}
                >
                  {isBusy ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <span className="text-xl tabular-nums leading-tight">{meta.label}</span>
                      <span className="text-[9px] leading-none mt-0.5">{c.tier === "B_plus" ? "جملة" : c.tier === "C_plus" ? "وسط" : "عادي"}</span>
                    </>
                  )}
                </button>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <h3 className="font-bold text-sm text-slate-900 truncate">
                      {c.customer_name || c.phone}
                    </h3>
                    {c.customer_id && (
                      <span className="bg-[#3B82F6]/15 text-[#1E3A5F] text-[10px] font-bold rounded-full px-1.5 py-0.5 flex items-center gap-0.5">
                        <Tablet className="w-2.5 h-2.5" />
                        {c.customer_id}
                      </span>
                    )}
                    {c.customer_type === "regular" && (
                      <span className="bg-slate-100 text-slate-600 text-[9px] font-bold rounded-full px-1.5 py-0.5">عادي</span>
                    )}
                  </div>
                  {c.market_name && <div className="text-[11px] text-slate-500 truncate">{c.market_name}</div>}
                  <div className="text-[11px] text-slate-400" dir="ltr">{c.phone}</div>
                </div>

                {/* Order counters */}
                <div className="flex-shrink-0 grid grid-cols-2 gap-1.5 text-center min-w-[120px]">
                  <div
                    className={`rounded-xl px-2 py-1.5 border ${
                      c.orders_today > 0
                        ? "bg-emerald-50 border-emerald-200"
                        : "bg-slate-50 border-slate-200"
                    }`}
                    title="طلبات اليوم"
                  >
                    <div className="text-[9px] font-bold text-slate-500">اليوم</div>
                    <div
                      className={`text-base font-extrabold tabular-nums ${
                        c.orders_today > 0 ? "text-emerald-700" : "text-slate-400"
                      }`}
                    >
                      {c.orders_today}
                    </div>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-xl px-2 py-1.5" title="آخر 7 أيام">
                    <div className="text-[9px] font-bold text-slate-500">7 أيام</div>
                    <div className="text-base font-extrabold text-[#1E3A5F] tabular-nums">{c.orders_7d}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Tier legend at bottom */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
        {Object.entries(TIER_META).map(([key, m]) => (
          <div
            key={key}
            className={`${m.bg} border-2 ${m.border} rounded-2xl p-3 flex items-start gap-2`}
          >
            <div className={`w-10 h-10 rounded-xl bg-white border-2 ${m.border} flex items-center justify-center font-extrabold ${m.text}`}>
              {m.label}
            </div>
            <div className="flex-1">
              <div className={`font-bold text-sm ${m.text}`}>{m.name}</div>
              <div className="text-[11px] text-slate-600 leading-relaxed">{m.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
